/**
 * pageCondition.ts
 * Per-page display conditions: decide, for one record (a group of Excel rows),
 * which physical pages of the template are printed at all.
 *
 * A "page" here is one Word section of the template, identified by its 0-based
 * index (`pageId`). A page without a rule is always applicable, so adding this
 * feature changes nothing for templates nobody configured.
 *
 * Evaluation is per record, never per row: the record is the whole group, so a
 * row-level operator asks "does any row of the group satisfy it" and the AND
 * combinator asks "does any single row satisfy the whole expression" — that is
 * what reproduces the Excel rule "∃ row with Mã SP ∈ {…} AND Số lượng > 0".
 */

/** Operators evaluated against a single row. */
export const ROW_OPERATORS = [
  "equals",
  "not_equals",
  "in",
  "not_in",
  "contains",
  "not_contains",
  "is_empty",
  "is_not_empty",
  "gt",
  "gte",
  "lt",
  "lte",
] as const;

/** Operators evaluated against the whole record (aggregate over its rows). */
export const AGGREGATE_OPERATORS = ["sum_gt", "sum_gte", "sum_lt", "sum_lte"] as const;

export type RowOperator = (typeof ROW_OPERATORS)[number];
export type AggregateOperator = (typeof AGGREGATE_OPERATORS)[number];
export type PageConditionOperator = RowOperator | AggregateOperator;

/** Operators that compare against a numeric threshold rather than a value list. */
export const NUMERIC_OPERATORS: PageConditionOperator[] = [
  "gt",
  "gte",
  "lt",
  "lte",
  "sum_gt",
  "sum_gte",
  "sum_lt",
  "sum_lte",
];

/** Operators that need no value at all. */
export const VALUELESS_OPERATORS: PageConditionOperator[] = ["is_empty", "is_not_empty"];

export const OPERATOR_LABELS: Record<PageConditionOperator, string> = {
  equals: "bằng",
  not_equals: "khác",
  in: "thuộc một trong các giá trị",
  not_in: "không thuộc các giá trị",
  contains: "chứa",
  not_contains: "không chứa",
  is_empty: "không có dữ liệu",
  is_not_empty: "có dữ liệu",
  gt: "lớn hơn",
  gte: "lớn hơn hoặc bằng",
  lt: "nhỏ hơn",
  lte: "nhỏ hơn hoặc bằng",
  sum_gt: "tổng lớn hơn",
  sum_gte: "tổng lớn hơn hoặc bằng",
  sum_lt: "tổng nhỏ hơn",
  sum_lte: "tổng nhỏ hơn hoặc bằng",
};

export interface PageCondition {
  id: string;
  /** An Excel column of the sheet in use — never free text. */
  field: string;
  operator: PageConditionOperator;
  /** Selected distinct values (multi-select) for the value-list operators. */
  values: string[];
  /** Threshold for the numeric and aggregate operators. */
  threshold?: number;
}

export type PageConditionCombinator = "AND" | "OR";

export interface PageConditionRule {
  enabled: boolean;
  combinator: PageConditionCombinator;
  conditions: PageCondition[];
  /**
   * Also drop the detail rows that fail the rule, so a quarantine sheet lists
   * only quarantine products instead of the whole order. Defaults to on.
   */
  filterRows?: boolean;
}

/** pageId (as a string key, JSON-friendly) → rule. */
export type PageConditionMap = Record<string, PageConditionRule>;

export interface PageConditionOutcome {
  pageId: number;
  /** False only when a rule was evaluated and the record failed it. */
  applicable: boolean;
  /** True when the page carries an enabled rule with at least one condition. */
  hasRule: boolean;
  /** Human-readable explanation, filled when the page is skipped. */
  reason: string;
}

export function isAggregateOperator(op: PageConditionOperator): op is AggregateOperator {
  return (AGGREGATE_OPERATORS as readonly string[]).includes(op);
}

export function createEmptyRule(): PageConditionRule {
  return { enabled: true, combinator: "AND", conditions: [], filterRows: true };
}

function text(value: unknown): string {
  return value == null ? "" : String(value).trim();
}

function lower(value: unknown): string {
  return text(value).toLowerCase();
}

/**
 * Excel cells arrive as numbers, as strings, and as strings carrying thousand
 * separators; all three must compare as numbers.
 */
export function toNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const raw = text(value);
  if (!raw) return null;
  const cleaned = raw.replace(/[\s ']/g, "").replace(/,/g, ".");
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

/** A condition the user has not finished filling in must not filter anything. */
export function isConditionComplete(condition: PageCondition): boolean {
  if (!condition.field) return false;
  if (VALUELESS_OPERATORS.includes(condition.operator)) return true;
  if (NUMERIC_OPERATORS.includes(condition.operator)) {
    return typeof condition.threshold === "number" && Number.isFinite(condition.threshold);
  }
  return condition.values.length > 0;
}

function matchesRow(condition: PageCondition, row: Record<string, unknown>): boolean {
  const cell = row[condition.field];
  const cellText = lower(cell);
  const values = condition.values.map((v) => v.trim().toLowerCase());

  switch (condition.operator) {
    case "equals":
      return values.includes(cellText);
    case "not_equals":
      return !values.includes(cellText);
    case "in":
      return values.includes(cellText);
    case "not_in":
      return !values.includes(cellText);
    case "contains":
      return values.some((v) => v !== "" && cellText.includes(v));
    case "not_contains":
      return !values.some((v) => v !== "" && cellText.includes(v));
    case "is_empty":
      return cellText === "";
    case "is_not_empty":
      return cellText !== "";
    default: {
      const num = toNumber(cell);
      const threshold = condition.threshold ?? 0;
      if (num === null) return false;
      if (condition.operator === "gt") return num > threshold;
      if (condition.operator === "gte") return num >= threshold;
      if (condition.operator === "lt") return num < threshold;
      if (condition.operator === "lte") return num <= threshold;
      return false;
    }
  }
}

export function sumOfField(rows: Record<string, unknown>[], field: string): number {
  return rows.reduce((acc, row) => acc + (toNumber(row[field]) ?? 0), 0);
}

function matchesAggregate(
  condition: PageCondition,
  rows: Record<string, unknown>[]
): { pass: boolean; sum: number } {
  const sum = sumOfField(rows, condition.field);
  const threshold = condition.threshold ?? 0;
  switch (condition.operator) {
    case "sum_gt":
      return { pass: sum > threshold, sum };
    case "sum_gte":
      return { pass: sum >= threshold, sum };
    case "sum_lt":
      return { pass: sum < threshold, sum };
    case "sum_lte":
      return { pass: sum <= threshold, sum };
    default:
      return { pass: true, sum };
  }
}

/** `[Mã SP] thuộc một trong các giá trị {A, B}` — used in the skip reason. */
export function describeCondition(condition: PageCondition): string {
  const label = OPERATOR_LABELS[condition.operator];
  if (VALUELESS_OPERATORS.includes(condition.operator)) {
    return `[${condition.field}] ${label}`;
  }
  if (NUMERIC_OPERATORS.includes(condition.operator)) {
    return `[${condition.field}] ${label} ${condition.threshold ?? 0}`;
  }
  return `[${condition.field}] ${label} {${condition.values.join(", ")}}`;
}

export function describeRule(rule: PageConditionRule): string {
  const joiner = rule.combinator === "AND" ? " VÀ " : " HOẶC ";
  return rule.conditions.filter(isConditionComplete).map(describeCondition).join(joiner);
}

/**
 * Evaluate one page's rule against one record.
 *
 * AND: the record passes when a *single* row satisfies every row-level
 * condition (aggregate conditions are checked against the record as a whole).
 * OR: the record passes when any row satisfies any row-level condition, or any
 * aggregate condition holds.
 */
export function evaluateRule(
  rule: PageConditionRule | undefined,
  rows: Record<string, unknown>[],
  pageId: number
): PageConditionOutcome {
  const conditions = (rule?.conditions ?? []).filter(isConditionComplete);
  if (!rule || !rule.enabled || conditions.length === 0) {
    return { pageId, applicable: true, hasRule: false, reason: "" };
  }

  const rowConditions = conditions.filter((c) => !isAggregateOperator(c.operator));
  const aggregateConditions = conditions.filter((c) => isAggregateOperator(c.operator));
  const pageLabel = `Trang ${pageId + 1}`;

  const aggregateResults = aggregateConditions.map((c) => ({
    condition: c,
    ...matchesAggregate(c, rows),
  }));
  const rowsPass =
    rowConditions.length === 0
      ? null
      : rule.combinator === "AND"
        ? rows.some((row) => rowConditions.every((c) => matchesRow(c, row)))
        : rows.some((row) => rowConditions.some((c) => matchesRow(c, row)));

  if (rule.combinator === "AND") {
    const failedAggregates = aggregateResults.filter((r) => !r.pass);
    const rowFailure = rowsPass === false;
    if (failedAggregates.length === 0 && !rowFailure) {
      return { pageId, applicable: true, hasRule: true, reason: "" };
    }
    const parts: string[] = [];
    if (rowFailure) {
      parts.push(
        `không có dòng nào thỏa ${rowConditions.map(describeCondition).join(" VÀ ")}`
      );
    }
    failedAggregates.forEach((r) => {
      parts.push(`${describeCondition(r.condition)} không thỏa (tổng = ${r.sum})`);
    });
    return {
      pageId,
      applicable: false,
      hasRule: true,
      reason: `${pageLabel} bị bỏ qua vì ${parts.join("; ")}.`,
    };
  }

  const anyAggregatePass = aggregateResults.some((r) => r.pass);
  if (anyAggregatePass || rowsPass === true) {
    return { pageId, applicable: true, hasRule: true, reason: "" };
  }
  return {
    pageId,
    applicable: false,
    hasRule: true,
    reason: `${pageLabel} bị bỏ qua vì không thỏa điều kiện nào: ${conditions
      .map(describeCondition)
      .join(" HOẶC ")}.`,
  };
}

/** Outcome of every page of the template for one record, in page order. */
export function evaluatePages(
  rules: PageConditionMap,
  rows: Record<string, unknown>[],
  pageCount: number
): PageConditionOutcome[] {
  return Array.from({ length: pageCount }, (_, pageId) =>
    evaluateRule(rules[String(pageId)], rows, pageId)
  );
}

/**
 * The rows a page's detail table should list: those satisfying the row-level
 * part of its rule. Aggregate conditions describe the record as a whole and so
 * never remove rows. Returns the rows untouched when the page filters nothing.
 */
export function filterRowsByRule(
  rule: PageConditionRule | undefined,
  rows: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (!rule || !rule.enabled || rule.filterRows === false) return rows;
  const conditions = (rule.conditions ?? [])
    .filter(isConditionComplete)
    .filter((c) => !isAggregateOperator(c.operator));
  if (conditions.length === 0) return rows;
  return rows.filter((row) =>
    rule.combinator === "AND"
      ? conditions.every((c) => matchesRow(c, row))
      : conditions.some((c) => matchesRow(c, row))
  );
}

/** Detail rows per page id, ready to hand to the template filler. */
export function rowsByPage(
  rules: PageConditionMap,
  rows: Record<string, unknown>[],
  pageCount: number
): Record<number, Record<string, unknown>[]> {
  const result: Record<number, Record<string, unknown>[]> = {};
  for (let pageId = 0; pageId < pageCount; pageId += 1) {
    result[pageId] = filterRowsByRule(rules[String(pageId)], rows);
  }
  return result;
}

/**
 * Keep only the rendered pages the record qualifies for. Called *before* page
 * numbering so a skipped page takes no number and does not inflate the total.
 *
 * `pageIdOf` maps a render position to its template page id; it differs from the
 * identity only when a single frame of the template is bound.
 */
export function selectApplicablePages<T>(
  pages: T[],
  outcomes: PageConditionOutcome[],
  pageIdOf: (renderIndex: number) => number = (i) => i
): T[] {
  return pages.filter((_, index) => outcomes[pageIdOf(index)]?.applicable !== false);
}

/** Distinct non-empty values of a column, for the value picker. */
export function distinctValues(
  rows: Record<string, unknown>[],
  field: string
): string[] {
  if (!field) return [];
  const seen = new Set<string>();
  for (const row of rows) {
    const value = text(row[field]);
    if (value) seen.add(value);
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b, "vi"));
}

export interface PageConditionValidation {
  warnings: string[];
  /** Fields referenced by a rule but absent from the sheet in use. */
  missingFields: string[];
  /** Page ids configured beyond the template's current page count. */
  missingPageIds: number[];
  valid: boolean;
}

/**
 * Re-check the saved rules against the data and template actually in play.
 * A rule pointing at a column or a page that no longer exists is reported, never
 * silently applied to the wrong thing.
 */
export function validatePageConditions(
  rules: PageConditionMap,
  headers: string[],
  pageCount: number
): PageConditionValidation {
  const headerSet = new Set(headers);
  const missingFields = new Set<string>();
  const missingPageIds: number[] = [];
  const warnings: string[] = [];

  Object.entries(rules).forEach(([key, rule]) => {
    const pageId = Number(key);
    const conditions = rule?.conditions ?? [];
    if (conditions.length === 0) return;
    if (!Number.isInteger(pageId) || pageId < 0 || pageId >= pageCount) {
      missingPageIds.push(pageId);
      warnings.push(
        `Điều kiện đang gắn với Trang ${pageId + 1} nhưng template hiện chỉ có ${pageCount} trang — hãy cấu hình lại.`
      );
      return;
    }
    conditions.forEach((c) => {
      if (c.field && !headerSet.has(c.field)) missingFields.add(c.field);
    });
  });

  missingFields.forEach((field) => {
    warnings.push(
      `Cột "${field}" dùng trong điều kiện hiển thị trang không có trong sheet đang chọn — hãy cấu hình lại điều kiện.`
    );
  });

  return {
    warnings,
    missingFields: Array.from(missingFields),
    missingPageIds,
    valid: warnings.length === 0,
  };
}
